import { Component, OnDestroy, OnInit } from '@angular/core';
import { ApiService } from '../shared/api.service';
import { AngularFireAuth } from '@angular/fire/auth';
import { AuthenticationService } from '../shared/authentication.service';
import { Subscription } from 'rxjs';

/**
 * API Key Interface
 */
export interface IApiKey {
  name: string; // Name of the API Key
  key: string; // The API Key
  issuedDate: number; // Date the API Key was issued
  issuedDateString?: string; // Converted to string from firebase format
  activated: boolean; // Whether the API Key is activated
  lastUsed: number; // Date the API Key was last used
  lastUsedString?: string; // Converted to string from firebase format
  visible?: boolean; // Whether the API Key is visible in the list. Displayed as stars if not.
}

@Component({
  selector: 'app-api-key',
  templateUrl: './api-key.component.html',
  styleUrls: ['./api-key.component.css']
})
export class ApiKeyComponent implements OnInit, OnDestroy {

  apiKeys: IApiKey[]; // List of API Keys
  idToken: string;
  deleteConfirmationText: string;
  apiKeyDeleted: IApiKey; // The API Key to be deleted
  deleteTextError = true; // Whether the delete confirmation text is invalid
  deleteModalSubmitted: boolean; // Whether the delete modal has been submitted
  generatedKeyName: string; // Name of the API Key to be generated
  keyNameError = false;
  loaded = false; // Whether the API Keys have been loaded
  uid: string; // The user's UID
  private authStateSubscription: Subscription;

  constructor(private apiService: ApiService, private angularFireAuth: AngularFireAuth,
              private authenticationService: AuthenticationService) {
  }

  ngOnInit(): void {
    this.authStateSubscription = this.angularFireAuth.authState.subscribe(async user => {
      if (user && user.uid) {
        // if user is not admin or org admin, return to home page
        if (this.authenticationService.userRole && !this.authenticationService.isAdmin && !this.authenticationService.isOrgAdmin) {
          window.location.href = '/';
        }
        const token = await user.getIdTokenResult();
        this.idToken = token.token; // Get the user's ID Token
        this.uid = this.authenticationService.userData.uid; // Get the user's UID

        // get api keys from user firestore data, or wait for 'user-data-loaded' event to get them
        if (this.authenticationService.userFirestoreData) {
          console.log(this.authenticationService.userFirestoreData);
          this.apiKeys = this.authenticationService.userFirestoreData.data?.apiKeys; // get api keys from user firestore data
          // convert properties issuedDate._seconds and lastUsed._seconds into local date/time strings for each api Key
          this.apiKeys.forEach(apiKey => {
            apiKey.issuedDateString = new Date(apiKey.issuedDate * 1000).toLocaleString();
            apiKey.lastUsedString = new Date(apiKey.lastUsed * 1000).toLocaleString();
          });

          this.loaded = true;
        } else {
          // set up event listener for custom event 'user-data-loaded' on the window object
          window.addEventListener('user-data-loaded', (event: CustomEvent) => {
            // get keys from event detail
            this.apiKeys = event.detail.data?.apiKeys;
            // convert properties issuedDate._seconds and lastUsed._seconds into local date/time strings for each api Key
            this.apiKeys.forEach(apiKey => {
              apiKey.issuedDateString = new Date(apiKey.issuedDate * 1000).toLocaleString();
              apiKey.lastUsedString = new Date(apiKey.lastUsed * 1000).toLocaleString();
            });
            this.loaded = true;
            console.log(event.detail);
          });
        }
      } else {
        // return to home page
        location.href = '/';
      }
    });

  }

  ngOnDestroy(): void {
    // unsubscribe from authStateSubscription
    if (this.authStateSubscription) {
      this.authStateSubscription.unsubscribe();
    }
  }

  /**
   * Changes the activation status of an api key
   * @param apiKey - api key to be activated/deactivated
   */
  async updateKeyActivation(apiKey: IApiKey): Promise<void> {
    // update api key activation status to the opposite of the current status
    apiKey.activated = !apiKey.activated;

    // update api key in the database
    try {
      await this.apiService.updateApiKey(apiKey, this.uid, this.idToken);
      apiKey.issuedDateString = new Date(apiKey.issuedDate * 1000).toLocaleString();
      apiKey.lastUsedString = new Date(apiKey.lastUsed * 1000).toLocaleString();
    } catch (e) {
      // revert activation status if api key update fails
      apiKey.activated = !apiKey.activated;
      console.log(e);
    }
  }

  /**
   * Delete api key
   * @param deletionKey - api key to delete
   */
  async deleteKey(deletionKey: IApiKey): Promise<void> {
    // set submitted to true to show errors if there are any
    this.deleteModalSubmitted = true;

    // if no errors, delete api key
    if (!this.deleteTextError) {
      try {
        await this.apiService.deleteApiKey(deletionKey, this.uid, this.idToken);
        // remove from apiKeys
        this.apiKeys = this.apiKeys.filter(key => key.key !== deletionKey.key);
      } catch (e) {
        console.log(e);
      }
    }
  }

  /**
   * Checks if the delete confirmation text matches
   * Used to prevent form submission if the text does not match
   */
  checkForErrors(): void {
    this.deleteTextError = this.deleteConfirmationText !== 'Delete';
  }

  /**
   * Generates a new API Key
   * @returns the generated API Key
   */
  async generateKey(): Promise<void> {
    try {
      const generatedKey = await this.apiService.generateApiKey(this.generatedKeyName, this.uid, this.idToken);

      // add generated key to apiKeys array if it doesn't exist, and generatedKey is not an empty object
      if (generatedKey && !this.apiKeys.find(key => key.name === generatedKey.name)) {
        // convert properties issuedDate and lastUsed into local date/time strings
        generatedKey.issuedDateString = new Date(generatedKey.issuedDate * 1000).toLocaleString();
        generatedKey.lastUsedString = new Date(generatedKey.lastUsed * 1000).toLocaleString();

        this.apiKeys.push(generatedKey);
      }

      // reset generated key name
      this.generatedKeyName = '';

      console.log(generatedKey);
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * Handler for copying UID to clipboard
   */
  copyUidToClipboard(): void {
    // copy this.uid to clipboard
    const textArea = document.createElement('textarea');
    textArea.value = this.uid;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    textArea.remove();
    // find copy button with id copy-uid-button and change text to 'UID Copied!'
    const copyButton = document.getElementById('copy-uid-button');
    copyButton.innerText = 'UID Copied!';
    // remove btn-primary class from copy button and add btn-success class
    copyButton.classList.remove('btn-primary');
    copyButton.classList.add('btn-success');

    // set timeout to change text back to 'Copy UID' after 2 seconds
    setTimeout(() => {
      copyButton.innerText = 'Copy UID';
      // remove btn-success class from copy button and add btn-primary class
      copyButton.classList.remove('btn-success');
      copyButton.classList.add('btn-primary');
    }, 2000);
  }
}
